import AnimationController from './animation';
import DataController from './data';
import PageController from './page';
import ResizeController from './resize';
import ScrollController from './scroll';

import { bindEvents, getRootOffsetBounds } from '../helpers';

import LoaderInitial from '../../../modules/loaders/loader-initial/script';
import LoaderPage from '../../../modules/loaders/loader-page/script';

// TODO: move skrollite to component

class UiController {

	constructor() {
		// elements

		this.el = {
			navigation: document.querySelector('#navigation-primary'),
			section: null,
			stickyDetails: document.body.querySelector('#sticky-details'),
			stickyDetailsTitleSpan: document.body.querySelector('#sticky-details .sticky-details__page-title span'),
			images: [],
			skrollites: []
		};

		// class variable groups

		this.controllers = {
			animation: null,
			resize: null
		};

		this.coords = {
			skrollites: []
		};

		this.delays = {
			skrollites: []
		};

		this.flags = {
			isInitialized: false,
			isFirstLoad: true,
			isPageLoaded: false,
			isShowing: false,
			skrollitesInViewport: []
		};

		this.loaders = {
			active: null,
			initial: new LoaderInitial(document.body.querySelector('#loader-initial')),
			page: new LoaderPage(document.body.querySelector('#loader-page'))
		};

		this.observers = {
			intersection: null
		};

		// bind events to class scope
		this.events = bindEvents(this);
	}

	initialize(section) {
		if (!this.flags.isInitialized) {
			this.el.section = section;

			this.addEventListeners();
			this.showLoader();

			this.flags.isInitialized = true;
		}
	}

	// event listeners

	addEventListeners() {
		if (!this.controllers.animation) {
			this.controllers.animation = AnimationController.set({
				update: this.events._onUpdate,
				render: this.events._onRender
			});
		}

		if (!this.controllers.resize) {
			this.controllers.resize = ResizeController.set({
				update: this.events._onResizeUpdate,
				render: this.events._onResizeRender
			});
		}
	}

	removeEventListener() {
		if (this.controllers.animation) {
			AnimationController.clear(this.controllers.animation);
			this.controllers.animation = null;
		}

		if (this.controllers.resize) {
			ResizeController.clear(this.controllers.resize);
			this.controllers.resize = null;
		}
	}

	// animation events

	_onUpdate() {
		this.measureScroll();
		return true;
	}

	_onRender() {
		this.renderScroll();
	}

	measureScroll() {
		const scrollTop = ScrollController.position;
		const scrollBottom = scrollTop + window.innerHeight;

		for (let i = 0; i < this.coords.skrollites.length; i++) {
			const skrollite = this.coords.skrollites[i];
			const $skrollite = this.el.skrollites[i];
			if ($skrollite.getAttribute('data-skrollite') === 'manual') {
				const manualTop = parseInt($skrollite.getAttribute('data-scroll-y'));
				const manualBottom = manualTop + skrollite.height;
				if (scrollTop <= manualBottom && scrollBottom > manualTop + window.innerHeight / 10) {
					this.flags.skrollitesInViewport[i] = true;
				} else {
					this.flags.skrollitesInViewport[i] = false;
				}
			} else {
				if (scrollTop <= skrollite.bottom && scrollBottom > skrollite.top) {
					this.flags.skrollitesInViewport[i] = true;
				} else {
					this.flags.skrollitesInViewport[i] = false;
				}
			}
		}
	}

	renderScroll() {
		if (this.flags.isPageLoaded) {
			for (let i = 0; i < this.flags.skrollitesInViewport.length; i++) {
				const $skrollite = this.el.skrollites[i];
				if (this.flags.skrollitesInViewport[i]) {
					if (!$skrollite.classList.contains('is-in-viewport')) {
						$skrollite.classList.add('is-in-viewport');
					}
					if (!$skrollite.classList.contains('is-not-first')) {
						$skrollite.classList.add('is-first-in');
					}
				} else {
					if ($skrollite.classList.contains('is-in-viewport')) {
						if ($skrollite.classList.contains('is-first-in') && !$skrollite.classList.contains('is-not-first')) {
							$skrollite.classList.add('is-not-first');
						}
						$skrollite.classList.remove('is-in-viewport', 'is-first-in');
					}
				}
			}
		}
	}

	// resize events

	_onResizeUpdate() {
		this.measureResize();
	}

	_onResizeRender() {

	}

	// measurement

	measureResize() {
		this.coords.skrollites = [];
		for (let i = 0; i < this.el.skrollites.length; i++) {
			const $skrollite = this.el.skrollites[i];
			this.coords.skrollites.push(getRootOffsetBounds($skrollite));
		}
	}

	// page in and out

	pageOut(showLoader = true) {
		return new Promise((resolve) => {
			if (showLoader) {
				this.showLoader();
			} else {
				this.hideNavigation();
			}

			this.disconnectImages();
			this.disconnectSkrollites();

			resolve();
		});
	}

	pageIn() {
		return new Promise((resolve) => {
			this.gatherImages();
			this.gatherSkrollites();
			this.renderStickyPageTitle();

			this.hideLoader().then(() => {
				this.measureResize();
				this.showNavigation();
				resolve();
			});
		});
	}

	//

	hideStickyPageTitle() {
		this.el.stickyDetails.classList.add('is-page-change');
	}

	renderStickyPageTitle() {
		DataController.loadPage(PageController.active.type, PageController.active.slug).then((page) => {
			this.el.stickyDetailsTitleSpan.innerHTML = page.node.title;
			this.el.stickyDetails.classList.remove('is-page-change');
		});
	}

	// main and page preloaders

	showLoader() {
		if (this.flags.isFirstLoad) {
			this.flags.isFirstLoad = false;
			this.loaders.active = this.loaders.initial;
			this.el.stickyDetails.classList.add('is-in');
		} else {
			this.loaders.active = this.loaders.page;
		}

		this.flags.isPageLoaded = false;

		this.el.stickyDetails.classList.add('is-loading');
		this.hideStickyPageTitle();
		this.loaders.active.show(this.flags.isFirstLoad);

		ScrollController.lockScroll();
	}

	hideLoader() {
		return new Promise((resolve) => {
			const showNextPage = () => {
				this.el.stickyDetails.classList.remove('is-loading');
				this.flags.isPageLoaded = true;

				ScrollController.unlockScroll();
				ScrollController.reset();

				resolve();
			};

			if (this.loaders.active) {
				this.loaders.active.hide().then(() => {
					this.loaders.active = null;
					showNextPage();
				}).catch(() => {
					console.warn('Loader Error'); // eslint-disable-line no-console
					showNextPage();
				});
			} else {
				showNextPage();
			}
		});
	}

	updateLoader() { }

	// images

	gatherImages() {
		this.el.images = this.el.section.querySelectorAll('.image');

		for (let i = 0; i < this.el.images.length; i++) {
			const $image = this.el.images[i];
			const $imageImg = this.el.images[i].querySelector('img, video');
			if ($imageImg && !$image.classList.contains('is-loading') && !$image.classList.contains('is-loaded')) {
				$image.classList.add('is-loading');
				$imageImg.addEventListener('load', this.events._onImageLoaded);
				$imageImg.addEventListener('loadeddata', this.events._onImageLoaded);
			}
		}
	}

	_onImageLoaded(e) {
		const $imageImg = e.target;
		const $image = $imageImg.closest('.image');

		$imageImg.removeEventListener('load', this.events._onImageLoaded);
		$imageImg.removeEventListener('loadeddata', this.events._onImageLoaded);
		$image.classList.remove('is-loading');
		$image.classList.add('is-loaded');

		if ($image.hasAttribute('data-image-auto-aspect')) {
			// console.log('auto aspect image', $image);
			$image.querySelector('.image__stage').style.paddingTop = `${$imageImg.naturalHeight / $imageImg.naturalWidth * 100}%`;
			ResizeController.measure();
		}
	}

	disconnectImages() {
		for (let i = 0; i < this.el.images.length; i++) {
			const $image = this.el.images[i];
			if ($image) {
				$image.removeEventListener('load', this.events._onImageLoaded);
			}
		}

		this.el.images = [];
	}

	disconnectSkrollites() {
		this.el.skrollites = [];
		this.coords.skrollites = [];
		this.flags.skrollitesInViewport = [];
	}

	// intersection observer

	gatherSkrollites() {
		this.el.skrollites = document.body.querySelectorAll('[data-skrollite]');
	}

	// navigation

	showNavigation() {
		this.el.navigation.classList.add('is-visible');
	}

	hideNavigation() {
		this.el.navigation.classList.remove('is-visible');
	}

}

export default new UiController();
